import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from 'react';
import { Input } from '@/shared/components/Input';
import { NORMAS_ORDER } from '../types';
import { ArticleCard } from './ArticleCard';
import { normalize } from '../utils/normalize';
export function InfractionsView({ data, target }) {
    const [norma, setNorma] = useState('LTSV');
    const [query, setQuery] = useState('');
    const [highlighted, setHighlighted] = useState(null);
    const containerRef = useRef(null);
    // React to navigation requests from the index of terms.
    // The norma + highlight state changes trigger a re-render; the actual scroll happens
    // in a separate effect below once the target element is in the DOM.
    useEffect(() => {
        if (!target)
            return;
        setNorma(target.norma);
        setQuery('');
        setHighlighted(target.article);
    }, [target]);
    // Scroll to the highlighted article. Polls briefly because the article list may not
    // have committed yet when norma was just changed. The page-level scroll lives on a
    // parent <main overflow-y-auto>, so we walk up to the closest scrollable ancestor
    // instead of trying to scroll the window.
    useEffect(() => {
        if (!target || !highlighted)
            return;
        if (norma !== target.norma)
            return; // wait until the norma state catches up
        let attempts = 0;
        let timeoutId;
        const tryScroll = () => {
            const sel = `[data-article-anchor="${cssEscape(target.article)}"]`;
            const el = containerRef.current?.querySelector(sel);
            if (el) {
                const scroller = findScrollableAncestor(el);
                if (scroller) {
                    const elRect = el.getBoundingClientRect();
                    const scRect = scroller.getBoundingClientRect();
                    const top = scroller.scrollTop + elRect.top - scRect.top - 16;
                    scroller.scrollTo({ top, behavior: 'smooth' });
                }
                else {
                    el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                }
                return;
            }
            if (attempts++ < 15) {
                timeoutId = window.setTimeout(tryScroll, 40);
            }
        };
        tryScroll();
        return () => {
            if (timeoutId !== undefined)
                window.clearTimeout(timeoutId);
        };
    }, [target, highlighted, norma]);
    // Clear the highlight after a few seconds so it's a transient cue.
    useEffect(() => {
        if (!highlighted)
            return;
        const t = window.setTimeout(() => setHighlighted(null), 2500);
        return () => window.clearTimeout(t);
    }, [highlighted]);
    const articles = data.infractions[norma] ?? [];
    const filtered = useMemo(() => {
        const q = normalize(query);
        if (!q)
            return articles;
        const tokens = q.split(' ').filter(Boolean);
        const matches = (text) => {
            const n = normalize(text);
            return tokens.every((t) => n.includes(t));
        };
        return articles
            .map((a) => ({
            ...a,
            rows: a.rows.filter((r) => matches(r.hecho) ||
                matches(`${r.norma} ${r.art} ${r.apd} ${r.opc}`) ||
                matches(a.title || '')),
        }))
            .filter((a) => a.rows.length > 0 || matches(a.title || '') || matches(`art ${a.number}`));
    }, [articles, query]);
    const totalRows = filtered.reduce((sum, a) => sum + a.rows.length, 0);
    return (_jsxs("div", { ref: containerRef, children: [_jsx("div", { className: "mb-4 -mx-1 flex flex-wrap gap-1 overflow-x-auto pb-1", children: NORMAS_ORDER.map((n) => {
                    const active = norma === n;
                    const count = data.infractions[n]?.reduce((s, a) => s + a.rows.length, 0) ?? 0;
                    return (_jsxs("button", { type: "button", onClick: () => setNorma(n), title: data.meta.normaLabels[n], className: "flex-shrink-0 rounded-lg px-3 py-2 text-sm font-semibold transition-colors", style: {
                            background: active ? 'rgba(var(--accent-rgb), 0.15)' : 'var(--surface)',
                            color: active ? 'var(--accent)' : 'var(--fg-2)',
                            border: '1px solid var(--border)',
                        }, children: [n, _jsx("span", { className: "ml-1.5 text-xs font-normal opacity-70", children: count })] }, n));
                }) }), _jsx("p", { className: "mb-3 text-xs text-gray-500 dark:text-gray-400", children: data.meta.normaLabels[norma] }), _jsx("div", { className: "mb-4", children: _jsx(Input, { type: "search", placeholder: "Buscar por palabra, art\u00EDculo o c\u00F3digo (p. ej. 'casco', '85.3', 'alcohol')\u2026", value: query, onChange: (e) => setQuery(e.target.value) }) }), _jsxs("div", { className: "mb-3 text-xs text-gray-500 dark:text-gray-400", children: [filtered.length, " art\u00EDculo", filtered.length === 1 ? '' : 's', " \u00B7 ", totalRows, " infracci\u00F3n", totalRows === 1 ? '' : 'es'] }), filtered.length === 0 ? (_jsxs("p", { className: "rounded-lg border p-8 text-center text-sm text-gray-500 dark:text-gray-400", style: { borderColor: 'var(--border)' }, children: ["Sin resultados para \u00AB", query, "\u00BB."] })) : (_jsx("div", { className: "space-y-3", children: filtered.map((a) => {
                    const isHl = highlighted === a.number;
                    return (_jsx(ArticleCard, { article: a, query: query, defaultOpen: query.trim().length > 0 || isHl, anchor: a.number, highlight: isHl }, `${norma}-${a.number}-${a.title}`));
                }) }))] }));
}
function cssEscape(s) {
    return s.replace(/(["\\])/g, '\\$1');
}
function findScrollableAncestor(node) {
    let el = node?.parentElement ?? null;
    while (el) {
        const cs = window.getComputedStyle(el);
        const oy = cs.overflowY;
        if ((oy === 'auto' || oy === 'scroll') && el.scrollHeight > el.clientHeight) {
            return el;
        }
        el = el.parentElement;
    }
    return null;
}
//# sourceMappingURL=InfractionsView.js.map